import React, {
    useCallback,
    useEffect,
    useRef,
    useState
} from 'react';
import ReactDOM from 'react-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import PropTypes from 'prop-types';

import styles from './EmailVerificationModal.module.css';
import closeButtonHoverImg from '../../static_files/closeButtonHover.svg';
import closeButtonImg from '../../static_files/closeButton.svg';
import robotThinkImg from '../../static_files/robotthink.svg';
import warningIcon from '../../static_files/warningImg.svg';

import {isLocalDevelopment, PRISMAX_BACKEND_URL} from '../../configs/config';
import {RECAPTCHA_V2_SITE_KEY} from '../../configs/authPublicConfig';
import {useRecaptcha} from '../../utils/useRecaptcha';

const CODE_LENGTH = 6;

const EmailVerificationModal = ({
                                    email,
                                    userId,
                                    onClose,
                                    onChangeEmail,
                                    onVerified
                                }) => {
    const {executeRecaptchaAction, isRecaptchaReady} = useRecaptcha();
    const recaptchaV2SiteKey = RECAPTCHA_V2_SITE_KEY;
    const isRecaptchaV2Configured = Boolean(recaptchaV2SiteKey);
    const canRequestSecurityCheck = isRecaptchaReady && isRecaptchaV2Configured;
    const shouldBypassRecaptcha = isLocalDevelopment;

    const [verificationCode, setVerificationCode] = useState('');
    const [secondsRemaining, setSecondsRemaining] = useState(30);
    const [isCountdownActive, setIsCountdownActive] = useState(false);

    const [errorMessage, setErrorMessage] = useState('');
    const [infoMessage, setInfoMessage] = useState('');

    const [isSendingCode, setIsSendingCode] = useState(false);
    const [isVerifyingCode, setIsVerifyingCode] = useState(false);

    const [recaptchaV2Token, setRecaptchaV2Token] = useState(null);
    const [showRecaptchaModal, setShowRecaptchaModal] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);
    const [mainCloseHover, setMainCloseHover] = useState(false);
    const [recaptchaCloseHover, setRecaptchaCloseHover] = useState(false);

    const inputRef = useRef(null);
    const countdownTimerRef = useRef(null);
    const initialRequestSentRef = useRef(false);

    const cleanupCountdownTimer = useCallback(() => {
        if (countdownTimerRef.current) {
            clearTimeout(countdownTimerRef.current);
            countdownTimerRef.current = null;
        }
    }, []);

    useEffect(() => {
        inputRef.current?.focus();
        return () => {
            cleanupCountdownTimer();
        };
    }, [cleanupCountdownTimer]);

    useEffect(() => {
        if (!isCountdownActive) {
            cleanupCountdownTimer();
            return;
        }

        if (secondsRemaining <= 0) {
            setIsCountdownActive(false);
            cleanupCountdownTimer();
            return;
        }

        countdownTimerRef.current = setTimeout(() => {
            setSecondsRemaining((prev) => prev - 1);
        }, 1000);

        return cleanupCountdownTimer;
    }, [isCountdownActive, secondsRemaining, cleanupCountdownTimer]);

    const sendVerificationCode = useCallback(async (v2Token) => {
        if (!email) {
            return;
        }

        if (!shouldBypassRecaptcha && !isRecaptchaReady) {
            setInfoMessage('Security verification is initializing. Please wait a moment and try again.');
            return;
        }

        setIsSendingCode(true);
        setErrorMessage('');
        setInfoMessage('');

        try {
            const normalizedEmail = email.trim().toLowerCase();
            const recaptchaToken = shouldBypassRecaptcha ? null : await executeRecaptchaAction('send_code');
            const response = await fetch(`${PRISMAX_BACKEND_URL}/auth/send-verification-code`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    email: normalizedEmail,
                    recaptchaToken,
                    recaptchaV2Token: v2Token
                })
            });

            const result = await response.json().catch(() => ({}));

            if (!response.ok || !result.success) {
                throw new Error(result?.msg || 'Failed to send verification code. Please try again.');
            }

            setInfoMessage('Verification code sent to your email.');
            setSecondsRemaining(30);
            setIsCountdownActive(true);
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to send verification code. Please try again.';
            setErrorMessage(message);
        } finally {
            setIsSendingCode(false);
        }
    }, [email, executeRecaptchaAction, isRecaptchaReady]);

    const requireRecaptchaVerification = useCallback(async (actionCallback) => {
        if (!isRecaptchaV2Configured) {
            setErrorMessage('Security verification is unavailable. Please contact support.');
            return;
        }

        if (!isRecaptchaReady) {
            setInfoMessage('Security verification is initializing. Please wait a moment.');
            return;
        }

        if (recaptchaV2Token) {
            await actionCallback(recaptchaV2Token);
            setRecaptchaV2Token(null);
        } else {
            setPendingAction(() => actionCallback);
            setShowRecaptchaModal(true);
        }
    }, [isRecaptchaReady, isRecaptchaV2Configured, recaptchaV2Token]);

    const verifyCode = useCallback(async (code) => {
        if (!email || code.length !== CODE_LENGTH || isVerifyingCode) {
            return;
        }

        setIsVerifyingCode(true);
        setErrorMessage('');

        try {
            const normalizedEmail = email.trim().toLowerCase();
            const token = localStorage.getItem('gatewayToken');

            if (!userId) {
                throw new Error('Missing user information. Please try again.');
            }

            const headers = {
                'Content-Type': 'application/json'
            };

            if (token) {
                headers.Authorization = `Bearer ${token}`;
            }

            const response = await fetch(`${PRISMAX_BACKEND_URL}/auth/update-user-profile-email/verify-code`, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    email: normalizedEmail,
                    code,
                    user_id: userId
                })
            });

            const result = await response.json().catch(() => ({}));

            if (!response.ok || !result.success) {
                throw new Error(result?.msg || 'Invalid verification code. Please try again.');
            }

            if (onVerified) {
                await onVerified(code);
            }
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Verification failed. Please try again.';
            setErrorMessage(message);
            setVerificationCode('');
        } finally {
            setIsVerifyingCode(false);
        }
    }, [email, isVerifyingCode, onVerified, userId]);

    useEffect(() => {
        if (verificationCode.length === CODE_LENGTH) {
            verifyCode(verificationCode);
        }
    }, [verificationCode, verifyCode]);

    useEffect(() => {
        if (!email || initialRequestSentRef.current) {
            return;
        }

        if (shouldBypassRecaptcha) {
            initialRequestSentRef.current = true;
            sendVerificationCode('test');
            return;
        }

        if (!isRecaptchaReady) {
            setInfoMessage('Security verification is initializing. Please wait a moment.');
            return;
        }

        initialRequestSentRef.current = true;

        requireRecaptchaVerification(async (token) => {
            await sendVerificationCode(token);
        });
    }, [email, isRecaptchaReady, requireRecaptchaVerification, sendVerificationCode]);

    const handleCodeChange = useCallback((event) => {
        const nextValue = event.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH);
        setVerificationCode(nextValue);
        setErrorMessage('');
    }, []);

    const handleCodeContainerClick = useCallback(() => {
        inputRef.current?.focus();
    }, []);

    const handleResend = useCallback(() => {
        if (isCountdownActive && secondsRemaining > 0) {
            return;
        }

        if (!canRequestSecurityCheck) {
            return;
        }

        requireRecaptchaVerification(async (token) => {
            await sendVerificationCode(token);
            setVerificationCode('');
        });
    }, [canRequestSecurityCheck, isCountdownActive, secondsRemaining, requireRecaptchaVerification, sendVerificationCode]);

    const handleOverlayClick = useCallback((event) => {
        if (event.target === event.currentTarget) {
            onClose?.();
        }
    }, [onClose]);

    const handleRecaptchaClose = useCallback(() => {
        setShowRecaptchaModal(false);
        setPendingAction(null);
        setRecaptchaCloseHover(false);
        onClose?.();
    }, [onClose]);

    const handleRecaptchaChange = useCallback(async (token) => {
        setRecaptchaV2Token(token);

        if (!token) {
            setErrorMessage('reCAPTCHA verification failed. Please try again.');
            return;
        }

        setShowRecaptchaModal(false);

        if (pendingAction) {
            try {
                await pendingAction(token);
            } finally {
                setPendingAction(null);
                setRecaptchaV2Token(null);
            }
        }
    }, [pendingAction]);

    const handleRecaptchaExpired = useCallback(() => {
        setRecaptchaV2Token(null);
        setErrorMessage('reCAPTCHA verification expired. Please try again.');
    }, []);

    const renderCodeBoxes = () => {
        const boxes = [];
        for (let i = 0; i < CODE_LENGTH; i += 1) {
            const char = verificationCode[i] || '';
            boxes.push(
                <div
                    key={`code-box-${i}`}
                    className={`${styles.codeBox} ${errorMessage ? styles.codeBoxError : ''}`}
                >
                    {char}
                </div>
            );
        }
        return boxes;
    };

    const disableResend = isSendingCode || (isCountdownActive && secondsRemaining > 0) || !canRequestSecurityCheck;

    return ReactDOM.createPortal(
        <>
            <div className={styles.overlay} onClick={handleOverlayClick}>
                <div className={styles.modalContainer} onClick={(event) => event.stopPropagation()}>
                    <button
                        type="button"
                        className={styles.closeButton}
                        onMouseEnter={() => setMainCloseHover(true)}
                        onMouseLeave={() => setMainCloseHover(false)}
                        onClick={onClose}
                    >
                        <img
                            src={mainCloseHover ? closeButtonHoverImg : closeButtonImg}
                            alt="Close"
                        />
                    </button>

                    <h2 className={styles.title}>Connect</h2>
                    <div style={{display:'flex', flexDirection:'column', alignItems:'flex-start'}}>
                        <p className={styles.subtitle}>
                            Enter your email to receive a one-time passcode
                        </p>

                        <p className={styles.title2}>
                            Enter verification code
                        </p>
                        <p className={styles.subtitle}>
                            We have sent an email to <span className={styles.emailHighlight}>{email}</span>
                        </p>
                    </div>
                    <div
                        className={styles.codeInputContainer}
                        onClick={handleCodeContainerClick}
                        role="presentation"
                    >
                        {renderCodeBoxes()}
                        <input
                            type="text"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            pattern="[0-9]*"
                            maxLength={CODE_LENGTH}
                            ref={inputRef}
                            className={styles.hiddenInput}
                            value={verificationCode}
                            onChange={handleCodeChange}
                        />
                    </div>

                    {errorMessage && (
                        <div className={styles.errorMessage}>
                            <img src={warningIcon} alt="Warning"/>
                            <span>{errorMessage}</span>
                        </div>
                    )}

                    {/*{infoMessage && !errorMessage && (*/}
                    {/*    <div className={styles.infoMessage}>{infoMessage}</div>*/}
                    {/*)}*/}

                    {isVerifyingCode && (
                        <div className={styles.statusMessage}>Verifying code...</div>
                    )}

                    <div className={styles.actions}>
                        <button
                            type="button"
                            className={`${styles.actionButton} ${disableResend ? styles.actionButtonDisabled : ''}`}
                            onClick={handleResend}
                            disabled={disableResend}
                        >
                            {isSendingCode ? 'Sending...' : 'Resend Email'}
                        </button>
                        <button
                            type="button"
                            className={styles.actionButton}
                            onClick={() => {
                                onChangeEmail?.();
                                onClose?.();
                            }}
                        >
                            Change Email
                        </button>
                    </div>

                    {isCountdownActive && secondsRemaining > 0 && (
                        <div className={styles.timerText}>
                            Please wait {secondsRemaining} seconds before sending another email
                        </div>
                    )}
                </div>
            </div>

            {showRecaptchaModal && (
                <div className={styles.recaptchaOverlay} onClick={handleRecaptchaClose}>
                    <div className={styles.recaptchaModal} onClick={(event) => event.stopPropagation()}>
                        <button
                            type="button"
                            className={styles.recaptchaCloseButton}
                            onMouseEnter={() => setRecaptchaCloseHover(true)}
                            onMouseLeave={() => setRecaptchaCloseHover(false)}
                            onClick={handleRecaptchaClose}
                        >
                            <img
                                src={recaptchaCloseHover ? closeButtonHoverImg : closeButtonImg}
                                alt="Close"
                            />
                        </button>
                        <img src={robotThinkImg} alt="Security check" className={styles.recaptchaIllustration}/>
                        <p className={styles.recaptchaTitle}>Hey! Are you human?</p>
                        <p className={styles.recaptchaText}>
                            {isRecaptchaV2Configured
                                ? 'Please confirm you are not a robot.'
                                : 'Security verification key is missing. Please contact support.'}
                        </p>
                        {isRecaptchaV2Configured && (
                            <div className={styles.recaptchaWidget}>
                                <ReCAPTCHA
                                    sitekey={recaptchaV2SiteKey}
                                    useRecaptchaNet
                                    onChange={handleRecaptchaChange}
                                    onExpired={handleRecaptchaExpired}
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>,
        document.getElementById('root')
    );
};

EmailVerificationModal.propTypes = {
    email: PropTypes.string.isRequired,
    userId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    onClose: PropTypes.func,
    onChangeEmail: PropTypes.func,
    onVerified: PropTypes.func
};

EmailVerificationModal.defaultProps = {
    onClose: undefined,
    onChangeEmail: undefined,
    onVerified: undefined,
    userId: null
};

export default EmailVerificationModal;
